import L from "leaflet";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { MonitoreoBrocaResponse } from "../../../../../modulos/monitoreo/broca/response/MonitoreoBrocaResponse";
import { _2d } from "../../../../../utils/numberUtils";
import {
  BaseMapRender,
  PopupConfig,
  PopupItem,
} from "../../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../../implementations/map/TopActions";

/** Métrica que colorea los lotes en el mapa. */
type MonitoreoBrocaMetrica = "infestacion" | "mortalidad";

/** Colores del semáforo, en literal (duplican `--s-low/mid/high-fill` de
 *  `tokens.css`): el mapa usa `preferCanvas: true`, y el canvas 2D de Leaflet
 *  no resuelve variables CSS en `fillColor`, solo colores ya resueltos. */
const NIVEL_FILL: Record<"low" | "mid" | "high", string> = {
  low: "rgba(226, 75, 74, 0.6)",
  mid: "rgba(239, 159, 39, 0.6)",
  high: "rgba(29, 158, 117, 0.6)",
};

/** Estilo cuando hay semana seleccionada pero el lote no tiene monitoreo esa
 *  semana (transparente, para distinguirlo de "sin semana elegida"). */
const STYLE_SIN_DATOS: L.PathOptions = {
  fillColor: "transparent",
  color: "rgb(224, 73, 13)",
  weight: 2.5,
  opacity: 1,
};

/** Mapa del módulo de Monitoreo Broca: lotes de la finca coloreados según la
 *  métrica activa (infestación real o mortalidad) para la semana seleccionada
 *  en el sidebar. Una sola instancia viva: el sidebar cambia los registros
 *  mostrados vía `setSemana`. La métrica se elige con el selector base del
 *  panel superior (`toolTipItems`). Ortomosaico sí, conteo no. */
export class MonitoreoBrocaMapRender extends BaseMapRender<MonitoreoBrocaResponse> {
  constructor() {
    super();
    // El mapa Leaflet (y `_groupList`) se arma async (connect): esperar a que
    // termine antes de aplicar una semana que haya llegado antes de tiempo.
    this.setOnMapInitializeEnd((map) => {
      this._mapReady = true;
      this._map = map;
      this._refresh();
      // Zoom/desplazamiento: notifica en vivo (no solo al soltar), para
      // sincronizar con otro mapa (p. ej. la vista dividida).
      map.on("move zoom", this._notifyViewChange);
    });
  }

  /** Muestra los lotes monitoreados en la semana elegida (la llama el sidebar). */
  setSemana(registros: MonitoreoBrocaResponse[]): void {
    this._registros = registros;
    this._refresh();
  }

  /** Registra un callback que se dispara con cada cambio de centro/zoom de
   *  este mapa (para sincronizar otro mapa, p. ej. en la vista dividida). */
  setOnViewChange(handler: (center: L.LatLng, zoom: number) => void): void {
    this._onViewChange = handler;
  }

  /** Aplica una vista (centro + zoom) sin animación y sin re-emitir el cambio
   *  (evita el loop A→B→A al sincronizar dos mapas). */
  setView(center: L.LatLng, zoom: number): void {
    if (!this._map) return;
    this._syncingView = true;
    this._map.setView(center, zoom, { animate: false });
    this._syncingView = false;
  }

  /** Sin datos propios: los registros llegan por `setSemana`. */
  protected doInitData(): Promise<MonitoreoBrocaResponse[]> {
    return Promise.resolve([]);
  }

  /** Monitoreo del lote dentro de los registros de la semana actual (o null). */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: MonitoreoBrocaResponse[],
  ): MonitoreoBrocaResponse | null {
    return this._registros.find((r) => r.loteId === lote.id) ?? null;
  }

  /** Quita el listener de zoom/desplazamiento. */
  protected doDispose(): void {
    this._map?.off("move zoom", this._notifyViewChange);
  }

  /** Colorea el lote según el semáforo de la métrica activa; sin registro,
   *  transparente si hay semana seleccionada, o el estilo por defecto si
   *  todavía no se eligió ninguna. */
  protected getStyle(
    lote: ClienteFincaLoteResponse,
    response: MonitoreoBrocaResponse | null,
  ): L.PathOptions {
    if (!response) {
      // Copia nueva: el click handler de la base muta el objeto devuelto
      // (`style.color = "yellow"` al seleccionar) — devolver la constante
      // compartida por referencia la dejaría corrompida en amarillo para
      // siempre (para todo lote sin dato, no solo el seleccionado).
      return this._registros.length > 0
        ? { ...STYLE_SIN_DATOS }
        : super.getStyle(lote, response);
    }
    const nivel =
      this._metrica === "infestacion"
        ? response.infestacionRealColor
        : response.mortalidadColor;
    return {
      fillColor: NIVEL_FILL[nivel],
      fillOpacity: 1,
      color: "rgb(224, 73, 13)",
      weight: 2.5,
      opacity: 1,
    };
  }

  /** Selector base (panel superior) con las dos métricas disponibles; al
   *  elegir una, actualiza `_metrica` y repinta. `get()` arma el mismo HTML
   *  (nombre + salto de línea + valor) que `_refresh()`: la base también hace
   *  su propio `bindTooltip` con este valor, y si difiriera del de `_refresh()`
   *  uno pisaría al otro con contenido distinto. Ortomosaico sí, conteo no. */
  protected getConfigTopActions(): ConfigTopActions<MonitoreoBrocaResponse> {
    return {
      toolTipItems: [
        {
          key: "infestacion",
          label: "Infestación",
          get: (response, lote) =>
            this._tooltipContent(lote, response, "infestacion"),
          // `onSelect` lo llama la base una vez por lote (no una sola vez): sin
          // esta guarda, `_refresh()` (que recorre todos los lotes) se repetiría
          // N veces por cada cambio de selector.
          onSelect: () => {
            if (this._metrica === "infestacion") return;
            this._metrica = "infestacion";
            this._refresh();
          },
        },
        {
          key: "mortalidad",
          label: "Mortalidad",
          get: (response, lote) =>
            this._tooltipContent(lote, response, "mortalidad"),
          onSelect: () => {
            if (this._metrica === "mortalidad") return;
            this._metrica = "mortalidad";
            this._refresh();
          },
        },
      ],
      withOrtomosaico: true,
      withConteo: false,
    };
  }

  /** Popup inicial (los items default); el contenido real lo pisa `_refresh()`
   *  en cuanto el mapa está listo, así que nunca se llega a ver esto. Sin
   *  edición (no es el mapa de configuración de lotes). */
  protected getPopupConfig(): PopupConfig<MonitoreoBrocaResponse> | null {
    return {
      bindItems: (lote, _response) => this.getDefaultPopupItems(lote),
      withEditLote: false,
    };
  }

  /** Si el mapa ya terminó de inicializar (`_groupList` listo). */
  private _mapReady = false;
  /** Registros de la semana actualmente mostrada. */
  private _registros: MonitoreoBrocaResponse[] = [];
  /** Métrica activa del selector. */
  private _metrica: MonitoreoBrocaMetrica = "infestacion";
  /** Instancia de Leaflet (para leer/aplicar centro y zoom). */
  private _map: L.Map | null = null;
  /** Notifica cambios de centro/zoom (sincronización con otro mapa). */
  private _onViewChange: ((center: L.LatLng, zoom: number) => void) | null =
    null;
  /** `true` mientras `setView` aplica una vista sincronizada (evita reemitir). */
  private _syncingView = false;

  /** Notifica el centro/zoom actual, salvo que el cambio venga de `setView`
   *  (sincronización externa, no debe rebotar de vuelta). */
  private _notifyViewChange = (): void => {
    if (!this._map || this._syncingView) return;
    this._onViewChange?.(this._map.getCenter(), this._map.getZoom());
  };

  /** Re-pinta estilo y tooltip de cada lote según los registros/métrica
   *  actuales. No-op hasta que el mapa termine de inicializar. El tooltip lo
   *  rebindeamos acá (no solo vía el selector base) para que también se
   *  actualice al cambiar de semana, sin tocar el selector. */
  private _refresh(): void {
    if (!this._mapReady) return;
    this._groupList.forEach((group) => {
      const response = this.doGetResponse(group.lote, this._registros);
      group.response = response;
      group.layer.setStyle(this.getStyle(group.lote, response));

      group.layer.unbindTooltip();
      group.layer
        .bindTooltip(
          this._tooltipContent(group.lote, response, this._metrica),
          {
            permanent: true,
            direction: "center",
            className: "leaflet-lote-tooltip",
          },
        )
        .openTooltip();

      // Popup: se actualiza el contenido del popup manual del grupo (no vía
      // getPopupConfig, que solo se lee una vez al inicializar el mapa) para
      // que también refleje la semana/métrica actual al cambiar de selección.
      // `setContent` ya dispara el refresco visual si está abierto — no hace
      // falta bindPopup/unbindPopup (eso reintroduciría el listener de click
      // interno de Leaflet, que ya no usamos en ningún otro mapa).
      group.popup?.setContent(this._popupContent(group.lote, response));
    });
  }

  /** HTML del popup de un lote: nombre + área (igual que el resto de los mapas)
   *  y, si tiene monitoreo esa semana, ambas métricas (no solo la activa en el
   *  selector superior). */
  private _popupContent(
    lote: ClienteFincaLoteResponse,
    response: MonitoreoBrocaResponse | null,
  ): string {
    const items: PopupItem[] = this.getDefaultPopupItems(lote);
    if (response) {
      items.push(
        {
          label: "Infestación",
          content: this._nivelBadge(
            response.infestacionRealColor,
            response.infestacionReal,
          ),
        },
        {
          label: "Mortalidad",
          content: this._nivelBadge(response.mortalidadColor, response.mortalidad),
        },
      );
    }
    const rows = items
      .map(
        (item) =>
          `<tr><td class="map-popup-label">${item.label}</td><td width="1" class="map-popup-content">${item.content}</td></tr>`,
      )
      .join("");
    return `<div class="map-popup"><div class="map-popup-header"><div class="sx-card-title">${lote.nombreLote}</div></div><div class="map-popup-body"><table><tbody>${rows}</tbody></table></div></div>`;
  }

  /** Badge coloreado según el semáforo (mismas clases que usa el sidebar). */
  private _nivelBadge(nivel: "low" | "mid" | "high", valor: number): string {
    return `<span class="sx-badge mon-broca-nivel mon-broca-nivel--${nivel}">${_2d(valor)}%</span>`;
  }

  /** HTML del tooltip permanente de un lote: nombre y, si tiene monitoreo esa
   *  semana, el valor de `metrica` en la línea de abajo. Lo usan tanto el
   *  `get()` del selector base como `_refresh()`, para que ambos produzcan
   *  siempre el mismo contenido. */
  private _tooltipContent(
    lote: ClienteFincaLoteResponse,
    response: MonitoreoBrocaResponse | null,
    metrica: MonitoreoBrocaMetrica,
  ): string {
    if (!response) return lote.nombreLote;
    const valor = _2d(
      metrica === "infestacion" ? response.infestacionReal : response.mortalidad,
    );
    return `<div class="lote-tooltip-stack"><span class="lote-tooltip-nombre">${lote.nombreLote}</span><span style="font-size:var(--sx-text-sm);opacity:0.95">${valor}%</span></div>`;
  }
}
